<template>
  <div class="error-ranking-chart">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default {
  name: 'ErrorRankingChart',
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chart: null,
      isCreating: false
    };
  },
  mounted() {
    // Add a small delay to ensure data is available and DOM is stable
    setTimeout(() => {
      this.$nextTick(() => {
        this.createChart();
      });
    }, 100);
  },
  watch: {
    data: {
      deep: true,
      handler() {
        // Add delay to ensure data is stable before updating
        setTimeout(() => {
          this.$nextTick(() => {
            this.updateChart();
          });
        }, 50);
      }
    }
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    createChart() {
      // Prevent multiple simultaneous chart creations
      if (this.isCreating) {
        return;
      }
      
      this.isCreating = true;
      
      try {
        // Always destroy existing chart first to prevent conflicts
        this.destroyChart();
        
        // Validate data first
        if (!this.data || this.data.length === 0) {
          return;
        }

        if (!this.$refs.chartCanvas) {
          return;
        }
        
        // Get context with error handling
        let ctx;
        try {
          ctx = this.$refs.chartCanvas.getContext('2d');
        } catch (contextError) {
          console.error('ErrorRankingChart: Error getting canvas context:', contextError);
          return;
        }
        
        if (!ctx) {
          return;
        }

        // Sort data by value (count) in descending order and take top 10
        const sortedData = [...this.data]
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 10);

        // Prepare labels with truncation for display
        const labels = sortedData.map(item => {
          const label = item.label || item.error_message || 'Unknown Error';
          return label.length > 40 ? label.substring(0, 40) + '...' : label;
        });

        const values = sortedData.map(item => item.value || 0);

        this.chart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [{
              label: 'Error Count',
              data: values,
              backgroundColor: [
                '#0a3954',
                '#dc3545',
                '#ffc107',
                '#0a1821',
                '#082B3F',
                '#364b5c',
                '#17a2b8',
                '#6c757d',
                '#28a745',
                '#007bff'
              ],
              borderColor: [
                '#0a3954',
                '#dc3545',
                '#ffc107',
                '#0a1821',
                '#082B3F',
                '#364b5c',
                '#17a2b8',
                '#6c757d',
                '#28a745',
                '#007bff'
              ],
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false, // Disable animations to prevent context conflicts
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  title: (context) => {
                    const index = context[0].dataIndex;
                    const fullMessage = sortedData[index]?.full_message || sortedData[index]?.label || '';
                    return this.wrapText(fullMessage, 50);
                  },
                  label: (context) => {
                    const index = context.dataIndex;
                    const item = sortedData[index];
                    return [
                      `Count: ${context.parsed.y.toLocaleString()}`,
                      `Percentage: ${item?.percentage?.toFixed(1) || 0}%`
                    ];
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Number of Errors'
                },
                ticks: {
                  callback: function(value) {
                    return value.toLocaleString();
                  }
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Error Messages'
                },
                ticks: {
                  maxRotation: 45,
                  minRotation: 0
                }
              }
            }
          }
        });
      } catch (error) {
        console.error('ErrorRankingChart: Error creating chart:', error);
      } finally {
        this.isCreating = false;
      }
    },

    updateChart() {
      if (!this.chart) {
        this.createChart();
        return;
      }
      
      try {
        // Validate data first
        if (!this.data || this.data.length === 0) {
          return;
        }

        // Sort data by value (count) in descending order and take top 10
        const sortedData = [...this.data]
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 10);

        // Prepare labels with truncation for display
        const labels = sortedData.map(item => {
          const label = item.label || item.error_message || 'Unknown Error';
          return label.length > 40 ? label.substring(0, 40) + '...' : label;
        });

        const values = sortedData.map(item => item.value || 0);

        this.chart.data.labels = labels;
        this.chart.data.datasets[0].data = values;
        
        this.chart.update('none'); // Use 'none' animation mode for updates
      } catch (error) {
        console.error('ErrorRankingChart: Error updating chart:', error);
        this.destroyChart();
        this.$nextTick(() => {
          this.createChart();
        });
      }
    },

    destroyChart() {
      if (this.chart) {
        try {
          this.chart.destroy();
        } catch (error) {
          console.error('ErrorRankingChart: Error destroying chart:', error);
        } finally {
          this.chart = null;
        }
      }
    },

    wrapText(text, maxLength) {
      if (!text || text.length <= maxLength) return text;
      
      const words = text.split(' ');
      const lines = [];
      let currentLine = '';
      
      for (const word of words) {
        if ((currentLine + word).length <= maxLength) {
          currentLine += (currentLine ? ' ' : '') + word;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      }
      
      if (currentLine) lines.push(currentLine);
      return lines.slice(0, 3); // Limit to 3 lines
    }
  }
};
</script>

<style scoped>
.error-ranking-chart {
  position: relative;
  height: 400px;
  width: 100%;
}

canvas {
  max-height: 400px;
}
</style>
